import { fetchPageAssets } from '../../ducks/hostedAssets.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { LISTING_STATE_PUBLISHED } from '../../util/types';

export const ASSET_NAME = 'landing-page';

// actions for new listings section
export const FETCH_NEW_LISTINGS_REQUEST = 'app/LandingPage/FETCH_NEW_LISTINGS_REQUEST';
export const FETCH_NEW_LISTINGS_SUCCESS = 'app/LandingPage/FETCH_NEW_LISTINGS_SUCCESS';
export const FETCH_NEW_LISTINGS_ERROR = 'app/LandingPage/FETCH_NEW_LISTINGS_ERROR';

// actions for featured listing
export const FETCH_FEATURED_LISTINGS_REQUEST = 'app/LandingPage/FETCH_FEATURED_LISTINGS_REQUEST';
export const FETCH_FEATURED_LISTINGS_SUCCESS = 'app/LandingPage/FETCH_FEATURED_LISTINGS_SUCCESS';
export const FETCH_FEATURED_LISTINGS_ERROR = 'app/LandingPage/FETCH_FEATURED_LISTINGS_ERROR';

const initialState = {
  pagination: null,
  // State for new listings
  newlyAddedListings: [],
  newlyAddedListingsInProgress: false,
  newlyAddedListingsError: null,
  featuredListings: [],
  featuredListingsInProgress: false,
  featuredListingsError: null,
};

const landingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    // Reducer cases for new listings
    case FETCH_NEW_LISTINGS_REQUEST:
      return {
        ...state,
        newlyAddedListingsInProgress: true,
        newlyAddedListingsError: null,
      };
    case FETCH_NEW_LISTINGS_SUCCESS:
      return {
        ...state,
        newlyAddedListingsInProgress: false,
        newlyAddedListings: payload.listings,
      };
    case FETCH_NEW_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        newlyAddedListingsInProgress: false,
        newlyAddedListingsError: payload,
      };
    // reducer cases for featured listing
    case FETCH_FEATURED_LISTINGS_REQUEST:
      return {
        ...state,
        featuredListingsInProgress: true,
        featuredListingsError: null,
      };
    case FETCH_FEATURED_LISTINGS_SUCCESS:
      return {
        ...state,
        featuredListingsInProgress: false,
        featuredListings: payload.listings,
      };
    case FETCH_FEATURED_LISTINGS_ERROR:
      console.error(payload);
      return {
        ...state,
        featuredListingsInProgress: false,
        featuredListingsError: payload,
      };

    default:
      return state;
  }
};

export default landingPageReducer;

// Action creators for new listings
export const fetchNewListingsRequest = () => ({
  type: FETCH_NEW_LISTINGS_REQUEST,
});

export const fetchNewListingsSuccess = listings => ({
  type: FETCH_NEW_LISTINGS_SUCCESS,
  payload: { listings },
});

export const fetchNewListingsError = e => ({
  type: FETCH_NEW_LISTINGS_ERROR,
  error: true,
  payload: e,
});

//Action creators for featured listings:
export const fetchFeaturedListingsRequest = () => ({
  type: FETCH_FEATURED_LISTINGS_REQUEST,
});

export const fetchFeaturedListingsSuccess = listings => ({
  type: FETCH_FEATURED_LISTINGS_SUCCESS,
  payload: { listings },
});

export const fetchFeaturedListingsError = e => ({
  type: FETCH_FEATURED_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// Thunk to load new listings
export const fetchNewListings = (config) => async (dispatch, getState, sdk) => {
  dispatch(fetchNewListingsRequest());

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const searchParams = {
    perPage: 50,
    'fields.listing': ['title', 'price', 'state', 'publicData', 'images'],
    'fields.image': [
      'url',
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    include: ['images'],
    page: 1,
    per_page: 4,
    pub_states: LISTING_STATE_PUBLISHED, // Only fetch published listings
  };

  try {
    const response = await sdk.listings.query(searchParams);
    const listings = denormalisedResponseEntities(response);

    dispatch(fetchNewListingsSuccess(listings));
    return listings;
  } catch (e) {
    console.error('Error fetching new listings:', e); // eslint-disable-line no-console
    dispatch(fetchNewListingsError(e));
    return null;
  }
};

// Thunk to load featured listing
export const fetchFeaturedListings = (config) => async (dispatch, getState, sdk) => {
  dispatch(fetchFeaturedListingsRequest());

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const searchParams = {
    perPage: 20,
    'fields.listing': ['title', 'price', 'state', 'publicData', 'images'],
    'fields.image': [
      'url',
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    include: ['images'],
    page: 1,
    per_page: 4,
    pub_states: LISTING_STATE_PUBLISHED,
    pub_isFeatured: true,
  };

  try {
    const response = await sdk.listings.query(searchParams);
    const listings = denormalisedResponseEntities(response);

    dispatch(fetchFeaturedListingsSuccess(listings));
    return listings;
  } catch (e) {
    console.error('Error fetching featured listings:', e);
    dispatch(fetchFeaturedListingsError(e));
    return null;
  }
};

export const loadData = (params, search, config, sdk) => async dispatch => {
  const pageAsset = { landingPage: `content/pages/${ASSET_NAME}.json` };
  const pageAssetsPromise = dispatch(fetchPageAssets(pageAsset, true));
  dispatch(fetchNewListings(config));
  dispatch(fetchFeaturedListings(config));
  return pageAssetsPromise;
};
 